import {
  collection,
  doc,
  addDoc,
  getDocs,
  getDoc,
  updateDoc,
  query,
  orderBy,
  where
} from 'firebase/firestore';
import { db } from './config';

// Collection names
const COLLECTIONS = {
  ORDERS: 'orders',
  PRODUCTS: 'products'
};

// ============= ORDERS =============

/**
 * Create a new order in Firebase
 * @param {Object} orderData - Order information including customer, items, and shipping details
 * @returns {Promise<string>} - The generated order ID
 */
export const createOrder = async (orderData) => {
  try {
    // Generate a unique order ID
    const timestamp = Date.now();
    const randomNum = Math.floor(Math.random() * 1000);
    const orderId = `ORD-${timestamp}-${randomNum}`;

    const newOrder = {
      ...orderData,
      orderId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    const docRef = await addDoc(collection(db, COLLECTIONS.ORDERS), newOrder);
    console.log('Order created successfully:', orderId);

    return orderId;
  } catch (error) {
    console.error('Error creating order:', error);
    throw error;
  }
};

/**
 * Get all orders from Firebase
 * @returns {Promise<Array>} - Array of all orders
 */
export const getOrders = async () => {
  try {
    const q = query(collection(db, COLLECTIONS.ORDERS), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      ...doc.data(),
      firebaseId: doc.id
    }));
  } catch (error) {
    console.error('Error getting orders:', error);
    return [];
  }
};

/**
 * Get a single order by its order ID
 * @param {string} orderId - The order ID
 * @returns {Promise<Object|null>} - The order data or null if not found
 */
export const getOrderById = async (orderId) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.ORDERS),
      where('orderId', '==', orderId)
    );
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return { ...doc.data(), firebaseId: doc.id };
    }
    return null;
  } catch (error) {
    console.error('Error getting order:', error);
    return null;
  }
};

/**
 * Update order status
 * @param {string} orderId - The order ID
 * @param {string} status - New status (pending, processing, shipped, delivered, cancelled)
 * @returns {Promise<boolean>} - Success status
 */
export const updateOrderStatus = async (orderId, status) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.ORDERS),
      where('orderId', '==', orderId)
    );
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const orderDoc = querySnapshot.docs[0];
      const orderData = orderDoc.data();
      const previousStatus = orderData.status;

      // If changing to cancelled and order wasn't already cancelled, restore stock
      if (status === 'cancelled' && previousStatus !== 'cancelled') {
        console.log(`Order ${orderId} is being cancelled. Restoring stock...`);

        // Restore stock for all items in the order
        for (const item of orderData.items) {
          await restoreProductStock(item.productId, item.quantity);
        }
      }

      // Update the order status
      const docRef = doc(db, COLLECTIONS.ORDERS, orderDoc.id);
      await updateDoc(docRef, {
        status,
        updatedAt: new Date().toISOString()
      });

      console.log(`Order ${orderId} status updated to ${status}`);
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error updating order status:', error);
    throw error;
  }
};

// ============= PRODUCT STOCK MANAGEMENT =============

/**
 * Update product stock after an order is placed
 * @param {string} productId - The product ID
 * @param {number} quantitySold - Quantity to subtract from stock
 * @returns {Promise<boolean>} - Success status
 */
export const updateProductStock = async (productId, quantitySold) => {
  try {
    // Find the product document by product ID
    const q = query(collection(db, COLLECTIONS.PRODUCTS));
    const querySnapshot = await getDocs(q);
    const productDoc = querySnapshot.docs.find(doc => doc.data().id === productId);

    if (productDoc) {
      const currentProduct = productDoc.data();
      const currentStock = currentProduct.stock || 0;
      const newStock = Math.max(0, currentStock - quantitySold);

      await updateDoc(doc(db, COLLECTIONS.PRODUCTS, productDoc.id), {
        stock: newStock,
        updatedAt: new Date().toISOString()
      });

      console.log(`Updated stock for product ${productId}: ${currentStock} -> ${newStock}`);
      return true;
    } else {
      console.warn(`Product ${productId} not found`);
      return false;
    }
  } catch (error) {
    console.error('Error updating product stock:', error);
    throw error;
  }
};

/**
 * Restore product stock (when order is cancelled)
 * @param {string} productId - The product ID
 * @param {number} quantityToRestore - Quantity to add back to stock
 * @returns {Promise<boolean>} - Success status
 */
export const restoreProductStock = async (productId, quantityToRestore) => {
  try {
    // Find the product document by product ID
    const q = query(collection(db, COLLECTIONS.PRODUCTS));
    const querySnapshot = await getDocs(q);
    const productDoc = querySnapshot.docs.find(doc => doc.data().id === productId);

    if (productDoc) {
      const currentProduct = productDoc.data();
      const currentStock = currentProduct.stock || 0;
      const newStock = currentStock + quantityToRestore;

      await updateDoc(doc(db, COLLECTIONS.PRODUCTS, productDoc.id), {
        stock: newStock,
        updatedAt: new Date().toISOString()
      });

      console.log(`Restored stock for product ${productId}: ${currentStock} -> ${newStock}`);
      return true;
    } else {
      console.warn(`Product ${productId} not found`);
      return false;
    }
  } catch (error) {
    console.error('Error restoring product stock:', error);
    throw error;
  }
};

/**
 * Check if products are in stock
 * @param {Array} items - Array of {productId, quantity} objects
 * @returns {Promise<Object>} - Object with inStock boolean and outOfStockItems array
 */
export const checkProductsStock = async (items) => {
  try {
    const outOfStockItems = [];

    for (const item of items) {
      const q = query(collection(db, COLLECTIONS.PRODUCTS));
      const querySnapshot = await getDocs(q);
      const productDoc = querySnapshot.docs.find(doc => doc.data().id === item.productId);

      if (productDoc) {
        const product = productDoc.data();
        const availableStock = product.stock || 0;

        if (availableStock < item.quantity) {
          outOfStockItems.push({
            productId: item.productId,
            productName: product.name,
            requested: item.quantity,
            available: availableStock
          });
        }
      } else {
        outOfStockItems.push({
          productId: item.productId,
          requested: item.quantity,
          available: 0,
          message: 'Product not found'
        });
      }
    }

    return {
      inStock: outOfStockItems.length === 0,
      outOfStockItems
    };
  } catch (error) {
    console.error('Error checking product stock:', error);
    throw error;
  }
};
